import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material//ArrowDropDown';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { flowRight } from 'lodash';
import { CapTableDetailsModel, CompGroupListCreate } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withAction from 'components/WithAction/withAction';
import withDialog from 'components/WithDialog/withDialog';
import withLink from 'components/WithLink/withLink';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { CompGroupsContext } from 'context';
import { SelectValueViewerProps } from './SelectValueViewer.props';
import TextCell from './TextCell/TextCell';
import { getOptionSelected } from './utilities/getOptionSelected';
import { CustomSelectValueViewerProps } from './utilities/utilities.props';

const TEXT_OFFSET = 34; // 10px left offset and 24px right offset (icon)

const SelectValueViewer = (props: SelectValueViewerProps) => {
  const { value, cell, onDoubleClick, options, readOnly = false, useAliasList, useLabel, caseInsensitiveMatch } = props;

  const { format } = useContext(FeaturedSpreadsheetContext);
  const currency = format?.currency;

  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [selected, setSelected] = useState(value);

  const [{ captableInfo, fundList, capTableList }] = useStore() as unknown as UseStoreValues;

  const compGroups = useContext(CompGroupsContext);

  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const shouldDisplayTooltip = useMemo(() => textWidth + TEXT_OFFSET >= containerWidth, [textWidth, containerWidth]);
  const isTextTooltipEnabled = useMemo(
    () => shouldDisplayTooltip && !(cell.tooltipMessages?.length ?? 0),
    [cell.tooltipMessages?.length, shouldDisplayTooltip]
  );

  useEffect(() => {
    const shouldUseOptionMapper = cell.columnLegend || cell.insideLedger || cell.dropdown;
    const selectedValue = shouldUseOptionMapper
      ? getOptionSelected({
        dataProps: {
          props: props as unknown as CustomSelectValueViewerProps,
          fundList: fundList || [],
          captableInfo: captableInfo as CapTableDetailsModel,
          capTableList: capTableList || [],
          useLabel,
          caseInsensitiveMatch,
          group: compGroups?.group as CompGroupListCreate,
          currency,
          defaultOptionList: options,
        },
        useAliasList,
      })
      : value;
    setSelected(selectedValue);
  }, [
    cell,
    value,
    useAliasList,
    options,
    fundList,
    captableInfo,
    capTableList,
    useLabel,
    compGroups,
    currency,
    props,
    caseInsensitiveMatch,
  ]);

  let className = 'value-viewer';

  const id = useMemo(() => {
    let key = '';
    if (cell?.getFullKey) {
      key = cell.getFullKey();
    } else if (cell?.key) {
      key = `${selected}-${cell.key}`;
    } else {
      key = selected;
    }
    return `select-value-viewer-${key}`;
  }, [cell, selected]);

  // Custom double click handler for cells not in a spreadsheet component
  const handleDoubleClick = useCallback(() => {
    const event = new MouseEvent('dblclick', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    document.getElementById(id)?.dispatchEvent(event);
  }, [id]);

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    if (textRef?.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, [selected]);

  if (cell.readOnly || readOnly) {
    return (
      <Grid component="span" className={clsx(className, 'select-value-viewer')} ref={containerRef}>
        <TextCell textRef={textRef} shouldDisplayTooltip={isTextTooltipEnabled}>
          {selected}
        </TextCell>
      </Grid>
    );
  }

  className = 'select-value-viewer';

  // When a modifier key is held, defer to parent handlers (e.g. AI popover) instead of
  // entering edit mode. This is a safe no-op for non-AI cells.
  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    (onDoubleClick || handleDoubleClick)();
  };

  return (
    <Grid
      className={className}
      id={id}
      onClick={handleClick}
      onKeyDown={onDoubleClick}
      ref={containerRef}
      role="button"
      tabIndex={0}>
      <TextCell textRef={textRef} shouldDisplayTooltip={isTextTooltipEnabled}>
        {selected}
      </TextCell>

      <Grid component="span" className="dropdown-icon">
        <ArrowDropDownIcon />
      </Grid>
    </Grid>
  );
};

const enhance = flowRight(withTooltip, withDialog, withRequired, withLink, withAction);

export default enhance(SelectValueViewer);
